import { z } from "zod";
// The locations a tablet is set to (Relationships-Australia-WA/Services-Delivery-Technology-Upgrade#451): csg_location GUIDs chosen
// from the locations list, at least one. Constrained to UUIDs so they can't carry
// OData-filter metacharacters into the ICIS session query, and refused when one
// repeats — the database holds them as an array, which does not.
const LocationIdsSchema = z
    .guid()
    .array()
    .min(1)
    .refine((ids) => new Set(ids).size === ids.length, {
    message: "Each location can be chosen only once",
});
// Body of the enrol and "set this kiosk's locations" requests. A tablet still
// running a build from before a tablet could have several locations sends
// `{ locationId }`, and is served rather than refused — the settings page it shows
// must keep working until it reloads. Either way the route sees `locationIds`.
const KioskLocationsBodySchema = z
    .union([
    z.object({ locationIds: LocationIdsSchema }),
    // Strict, so a new-style body with a bad `locationIds` is refused for that
    // rather than slipping through on a stray `locationId`.
    z.strictObject({ locationId: z.guid() }),
])
    .transform((body) => ({
    locationIds: "locationIds" in body ? body.locationIds : [body.locationId],
}));
export const SetKioskLocationSchema = KioskLocationsBodySchema;
// First-time enrolment (ADR-025). Same body as setting the locations, but a
// distinct contract: this one creates the device and issues its credential.
export const EnrolKioskDeviceSchema = KioskLocationsBodySchema;
// The X-Kiosk-Fingerprint header: a FingerprintJS visitorId, a 128-bit hash as
// 32 lowercase hex characters. It identifies a tablet for the admin device list
// and authenticates nothing, so a value of any other shape is dropped rather than
// refused — a bad fingerprint must never stop a tablet being set up.
export const KioskFingerprintSchema = z.string().regex(/^[0-9a-f]{32}$/);
// The enrolled device behind the credential on this request, as the tablet itself
// sees it. Fetching it doubles as the credential check: an unenrolled or revoked
// device gets a 401 rather than a body. The client resolves the ids to display
// names against the locations list.
export const CurrentKioskDeviceSchema = z.object({
    locationIds: z.guid().array(),
    // Any one of locationIds. Kept indefinitely: a build from before a tablet could
    // have several locations requires it, and failing to parse this response puts
    // "We can't reach the server right now" on its welcome screen.
    locationId: z.string(),
});
// An enrolled tablet as the admin device list shows it. There is no device name:
// a tablet is identified by where it is and when it last called in, so lastSeenAt
// is what tells an operator which of two devices at a site is still in use and
// which has gone quiet (ADR-025). Null until the device makes its first request.
export const KioskDeviceSummarySchema = z.object({
    id: z.guid(),
    // Every location the tablet is set to, sorted by name. A name is null when the
    // id is not in the local ICIS location mirror — enrolment offers locations from
    // a live ICIS read, so a site added there but not yet mirrored resolves to no
    // name. The client falls back to the id, as the rollout viewer does: an
    // enrolled tablet must never be hidden from the list that revokes it.
    locations: z.object({ id: z.guid(), name: z.string().nullable() }).array(),
    status: z.enum(["active", "revoked"]),
    enrolledAt: z.iso.datetime(),
    lastSeenAt: z.iso.datetime().nullable(),
    // The tablet's FingerprintJS visitorId, so two records at a site can be told
    // apart as one tablet re-enrolled or two tablets. Identifies, never
    // authenticates. Null until the tablet first sends one.
    fingerprint: z.string().nullable(),
});
export const KioskDevicesResponseSchema = z.object({
    kioskDevices: KioskDeviceSummarySchema.array(),
});
